import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, AuthContext } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import InactivityTimer from './components/InactivityTimer';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import TramiteTypes from './pages/TramiteTypes';
import Caja from './pages/Caja';
import CajaAbonos from './pages/CajaAbonos';
import Reports from './pages/Reports';
import EmailMonitor from './pages/EmailMonitor';
import ApiLogs from './pages/ApiLogs';
import Settings from './pages/Settings';
import Profiles from './pages/Profiles';
import Welcome from './pages/Welcome';
import Audit from './pages/Audit';
import Transitos from './pages/Transitos';
import Concesionarios from './pages/Concesionarios';
import PlateManagement from './pages/PlateManagement';
import PlateInventoryDetail from './pages/PlateInventoryDetail';
import MisTramites from './pages/MisTramites';
import CuplPayments from './pages/CuplPayments';
import RangeRequests from './pages/RangeRequests';
import PlateDeliveries from './pages/PlateDeliveries';
import Liquidacion from './pages/Liquidacion';
import Impuestos from './pages/Impuestos';
import Eliminaciones from './pages/Eliminaciones';
import PuntosVenta from './pages/PuntosVenta';
import Zonas from './pages/Zonas';

import BandejaTransito from './pages/BandejaTransito';
import DataRecovery from './pages/DataRecovery';
import MyProfile from './pages/MyProfile';
import SoatExecutionLogs from './pages/SoatExecutionLogs';
import ConfigNovedades from './pages/ConfigNovedades';
import Soportes from './pages/Soportes';
import SoatSubmodulo from './pages/SoatSubmodulo';
import CobroEjecutados from './pages/CobroEjecutados';
import DocumentosSoporte from './pages/DocumentosSoporte';
import WhatsAppConfig from './pages/WhatsAppConfig';
import CambiosTramites from './pages/CambiosTramites';
import HistorialPlacas from './pages/HistorialPlacas';
import PagosTransito from './pages/PagosTransito';
import './App.css';

// Componente para manejar la redirección de la raíz
const RootRedirect = () => {
  const { user, token, loading } = React.useContext(AuthContext);
  
  if (loading) return null;
  if (!token) return <Navigate to="/login" />;
  
  const hasDashboard = user?.modules?.some(m => m.name === 'dashboard') || user?.role === 'superadmin';
  return hasDashboard ? <Navigate to="/dashboard" /> : <Navigate to="/welcome" />;
};

const getBasename = () => {
  const path = window.location.pathname;
  if (path && path.split('/').length > 1) {
    const segment = path.split('/')[1];
    const excluded = [
      'login', 'register', 'dashboard', 'users', 'tripletype', 'caja', 
      'caja-abonos', 'reports', 'email-monitor', 'api-logs', 'settings', 
      'profiles', 'audit', 'transitos', 'concesionarios', 'plates', 
      'mis-tramites', 'cupl-payments', 'bandeja-transito', 'data-recovery', 
      'soat-execution-logs', 'config-novedades', 'my-profile', 'welcome',
      'range-requests', 'plate-deliveries', 'soportes', 'soat',
      'liquidacion', 'impuestos', 'eliminaciones', 'puntos-venta', 'zonas',
      'cobro-ejecutados', 'documentos-soporte', 'whatsapp-config', 'cambios-tramites', 'historial-placas', 'pagos-transito'
    ];
    if (segment && !excluded.includes(segment.toLowerCase())) {
      return `/${segment}`;
    }
  }
  return '/';
};

function App() {
  return (
    <Router basename={getBasename()}>
      <ThemeProvider>
        <AuthProvider>
          <InactivityTimer timeoutMinutes={30} />
          <div className="theme-wrapper">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              
              {/* Rutas con Sidebar/Navbar */}
              {[
                { path: '/dashboard', element: <Dashboard />, module: 'dashboard' },
                { path: '/users', element: <Users />, module: 'users' },
                { path: '/tripletype', element: <TramiteTypes />, module: 'tripletype' },
                { path: '/caja', element: <Caja />, module: 'caja' },
                { path: '/caja-abonos', element: <CajaAbonos />, module: 'caja-abonos' },
                { path: '/reports', element: <Reports />, module: 'reports' },
                { path: '/email-monitor', element: <EmailMonitor />, module: 'email-monitor' },
                { path: '/api-logs', element: <ApiLogs />, module: 'api-logs' },
                { path: '/settings', element: <Settings /> },
                { path: '/profiles', element: <Profiles />, module: 'profiles' },
                { path: '/audit', element: <Audit />, module: 'audit' },
                { path: '/transitos', element: <Transitos />, module: 'transitos' },
                { path: '/concesionarios', element: <Concesionarios />, module: 'concesionarios' },
                { path: '/zonas', element: <Zonas />, module: 'zonas' },
                { path: '/plates', element: <PlateManagement />, module: 'plates' },
                { path: '/plates/inventory/:transito_id', element: <PlateInventoryDetail />, module: 'plates' },
                { path: '/mis-tramites', element: <MisTramites />, module: 'mis-tramites' },
                { path: '/eliminaciones', element: <Eliminaciones />, module: 'mis-tramites' },
                { path: '/cupl-payments', element: <CuplPayments />, module: 'cupl-payments' },
                { path: '/soat', element: <SoatSubmodulo />, module: 'soat-submodulo' },
                { path: '/bandeja-transito', element: <BandejaTransito />, module: 'bandeja-transito' },
                { path: '/data-recovery', element: <DataRecovery />, module: 'data-recovery' },
                { path: '/soat-execution-logs', element: <SoatExecutionLogs />, module: 'soat-execution-logs' },
                { path: '/config-novedades', element: <ConfigNovedades />, module: 'config-novedades' },
                { path: '/my-profile', element: <MyProfile /> },
                { path: '/welcome', element: <Welcome /> },
                { path: '/range-requests', element: <RangeRequests />, module: 'range-requests' },
                { path: '/plate-deliveries', element: <PlateDeliveries />, module: 'plate-deliveries' },
                { path: '/soportes', element: <Soportes />, module: 'soportes' },
                { path: '/liquidacion', element: <Liquidacion />, module: 'liquidacion' },
                { path: '/impuestos', element: <Impuestos />, module: 'impuestos' },
                { path: '/puntos-venta', element: <PuntosVenta />, module: 'concesionarios' },
                { path: '/cobro-ejecutados', element: <CobroEjecutados />, module: 'cobro-ejecutados' },
                { path: '/documentos-soporte', element: <DocumentosSoporte />, module: 'documentos-soporte' },
                { path: '/whatsapp-config', element: <WhatsAppConfig /> },
                { path: '/cambios-tramites', element: <CambiosTramites />, module: 'cambios-tramites' },
                { path: '/historial-placas', element: <HistorialPlacas />, module: 'historial-placas' },
                { path: '/pagos-transito', element: <PagosTransito />, module: 'pagos-transito' }
              ].map(route => (
                <Route
                  key={route.path}
                  path={route.path}
                  element={
                    <ProtectedRoute moduleName={route.module}>
                      {route.element}
                    </ProtectedRoute>
                  }
                />
              ))}
              
              <Route path="/" element={<RootRedirect />} />
            </Routes>
          </div>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
